import React, { type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next/src';
import Button from '@atlaskit/button/new';
import { Flex } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { BACKLOG_CREATE_SPRINT_NUDGE_ID } from '@atlassian/jira-onboarding-quickstart-core/src/common/constants';
import { useNudgeStep } from '@atlassian/jira-onboarding-quickstart-core/src/services';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ConditionalNudgeWrapper } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/conditional-nudge-wrapper';
import { BacklogCreateSprintNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-create-sprint/async.tsx';
import { useSprintLifecycleContext } from '../../../../services/sprint-lifecycle-context';
import { DisabledButtonHelper } from '../../common/sprint-actions/disabled-button-helper';
import messages from '../messages';

interface CreateSprintButtonProps {
	onCreateSprint: (analyticsEvent: UIAnalyticsEvent) => void;
}

export const CreateSprintButton = ({ onCreateSprint }: CreateSprintButtonProps) => {
	const [nudgeStep] = useNudgeStep();
	const { formatMessage } = useIntl();
	const { canManageSprints, isCreateSprintButtonDisabled } = useSprintLifecycleContext();

	const handleCreateSprint = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'createSprint');
		onCreateSprint(analyticsEvent);
	};

	return (
		<Flex
			direction="row"
			alignItems="center"
			testId="software-backlog.card-list.card-list-header.create-sprint-button.create-sprint-button-container"
		>
			<ConditionalNudgeWrapper
				Wrapper={BacklogCreateSprintNudgeAsync}
				conditionsToApplyWrapper={
					fg('jsw-perf-onboarding-backlog-create-sprint-load')
						? nudgeStep === BACKLOG_CREATE_SPRINT_NUDGE_ID
						: true
				}
			>
				{!canManageSprints && fg('jsw-jet-yamato-fe-permissions-seq-sprint-actions') ? (
					<DisabledButtonHelper>{formatMessage(messages.createSprint)}</DisabledButtonHelper>
				) : (
					<Button
						onClick={handleCreateSprint}
						isDisabled={isCreateSprintButtonDisabled}
						testId="software-backlog.card-list.card-list-header.create-sprint-button"
						spacing="compact"
					>
						{formatMessage(messages.createSprint)}
					</Button>
				)}
			</ConditionalNudgeWrapper>
		</Flex>
	);
};
